import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Observable, Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import ODataFilterBuilder from 'odata-filter-builder';

import {
  IClassroom,
  IParamObj,
  IProgram,
  IProgramClassroomSearchQuery,
} from '../../models';
import { ClassroomService } from '../../services/classroom.service';

/** 
 * Odata query of Classroom table with associated Program expanded 
 * instead of querying LookupAssociatedClassProgramChild table
 * */
@Component({
  selector: 'app-search-program-classroom-odata',
  templateUrl: './search-program-classroom-odata.component.html',
  styleUrls: ['./search-program-classroom-odata.component.scss']
})
export class SearchProgramClassroomODataComponent implements OnInit, OnChanges {
  @Input() currentProgram: IProgram = null;
  @Input() currentClassroom: IClassroom = null;
  @Input() vqb5: boolean | null = null;
  @Input() searchAll: boolean = false; // If true, include ALL programs/classrooms
  @Input() regionFilter: number = null;
  @Output() select$ = new EventEmitter<IClassroom>();

  public isNotSearching: boolean = true;
  public searchResults$: Observable<Array<IClassroom>>;
  private searchText$ = new Subject<string>();
  private oDataFilter = ODataFilterBuilder();
  public searchQuery: IProgramClassroomSearchQuery;

  constructor(
    private classroomService: ClassroomService
  ) { }

  ngOnInit() {
    this.getSearchResults();
  }

  /**
   * When change coach/region in parent component,
   * clear selected prog/class (switching regions so no longer valid selections)
   * and if searching, update region search filter/results
   * emit change to form
   * ignore if region filter hasn't changed
   * @param changes 
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['regionFilter'] != null 
      && !changes['regionFilter'].firstChange 
      && changes['regionFilter'].previousValue != changes['regionFilter'].currentValue) {
      this.currentProgram = null;
      this.currentClassroom = null;
      this.select$.emit(null);

      if (!this.isNotSearching) {
        this.searchQuery.programRegionId = this.regionFilter;
        this.updateSearch(null);
      }
    }
  }

  onSelect(selectedSearchResult: IClassroom) {
    this.currentProgram = selectedSearchResult.program;
    this.currentClassroom = selectedSearchResult;
    this.isNotSearching = true;
    this.select$.emit(selectedSearchResult);
  }

  public getEventValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  public updateSearch(searchString: string) {
    this.buildFilter();
    this.searchText$.next(searchString);
  }

  public initSearchFilters() {
    this.isNotSearching = false;
    this.searchQuery = {
      programRegionId: this.regionFilter,
      vacSiteId: '',
      programName: '',
      programAddress: '',
      classroom: '',
    };
  }

  /** Dynamic filter by search query, coach's region, and special event type logic */
  private buildFilter() {
    this.oDataFilter = ODataFilterBuilder("and")
      .contains('Program/vacSiteId', this.searchQuery.vacSiteId)
      .contains('Program/name', this.searchQuery.programName)
      .contains('Program/address', this.searchQuery.programAddress)
      .and(ODataFilterBuilder('or')
        .contains('name', this.searchQuery.classroom)
        .contains('stateId', this.searchQuery.classroom)
      );

    this.searchQuery.programRegionId && this.oDataFilter.eq('Program/regionId', this.searchQuery.programRegionId);

    // Filter by VQB5 if !null
    if(this.vqb5 !== null){
      this.oDataFilter.eq('Program/isVbq5', this.vqb5);
    }

    // Default filter by Program.IsCea and Classroom.IsActive unless want to search all
    if(this.searchAll === false) {
      this.oDataFilter.eq('Program/isCea', true);
      this.oDataFilter.eq('isActive', true);
    }
    
  }

  /**
   * Get search results from odata Classrooms entity w/Program expanded
   * Filter based on provided search parameters
   * Return top 8 results, ordered by VAC Site ID ASC, then by Classroom.StateId ASC
   */
  private getSearchResults() {
    this.searchResults$ = this.searchText$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(() => {
        const params = { 
          $expand: 'Program',
          $filter: this.oDataFilter.toString(),
          $orderby: 'Program/vacSiteId asc, stateId asc',
          $top: 8 
        } as IParamObj;
        
        return this.classroomService.get(params);
      })
    );
  }
}
